import type { ImportCommentary, ImportDocument, ImportNode, NodeType } from '@cca/corpus-shared';
import type { ParsedDocx } from './docxParse';

/**
 * Map parsed .docx paragraphs → an ImportDocument, using the styles/formatting
 * the CCAANZ liturgy masters use (derived from the Lord's Supper MASTER):
 *
 *   Title            → document title
 *   Heading1/4       → heading (top-level section)
 *   Subtitle         → rubric (the directive); becomes the "current rubric"
 *   italic-only body → rubric too (alt directions, e.g. "Or the two great…")
 *   body, bold run   → response (Congregation)
 *   body, plain      → spoken (Leader)
 *   footnote on a ¶  → commentary on that line (Commission discussion)
 *
 * Lines hang under the current rubric so references compute as r{N}-l/c{k}.
 * Leader/response nesting is left flat; the author refines in the editor.
 */

const isHeadingStyle = (s: string): boolean => /^Heading[1-6]$/.test(s);

export interface MappedImport {
  doc: ImportDocument;
  counts: {
    headings: number;
    rubrics: number;
    leader: number;
    congregation: number;
    notes: number;
  };
}

export function mapToDoc(parsed: ParsedDocx): MappedImport {
  const { paragraphs, footnotes } = parsed;
  const nodes: ImportNode[] = [];
  const commentary: ImportCommentary[] = [];
  const counts = { headings: 0, rubrics: 0, leader: 0, congregation: 0, notes: 0 };

  let title = '';
  let currentRubric: string | null = null;
  let seq = 0;
  let tn = 0;
  // The last emitted node, so consecutive same-type Leader/Congregation
  // paragraphs collapse into ONE multi-line block (one box) — e.g. the nine
  // lines of the Collect for Purity become a single Congregation node.
  let last: ImportNode | null = null;

  const add = (type: NodeType, text: string, parentTempId: string | null): ImportNode => {
    const node: ImportNode = {
      tempId: `t${tn++}`,
      parentTempId,
      orderIndex: ++seq,
      type,
      lines: [{ runs: [{ text }] }],
    };
    nodes.push(node);
    return node;
  };

  for (const p of paragraphs) {
    const text = p.text.trim();
    if (p.style === 'Title') {
      if (!title && text) title = text;
      continue;
    }
    if (!text && p.footnoteIds.length === 0) continue; // skip blank lines

    let node: ImportNode;
    // Standard mapping (see docs/liturgy-doc-format.md): ITALIC = a direction
    // (rubric) — checked first, since some directions are styled as headings too;
    // a HEADING style = a section Header; Subtitle / red = a rubric; BOLD = a
    // Congregation response; plain text = the Leader.
    if (p.italic && !p.bold) {
      node = add('rubric', text, null);
      currentRubric = node.tempId;
      counts.rubrics++;
    } else if (isHeadingStyle(p.style)) {
      node = add('heading', text, null);
      currentRubric = null;
      counts.headings++;
    } else if (p.style === 'Subtitle' || p.rubricColor) {
      node = add('rubric', text, null);
      currentRubric = node.tempId;
      counts.rubrics++;
    } else {
      const type: NodeType = p.bold ? 'response' : 'spoken';
      // Congregation responses DEFAULT to nesting under the preceding Leader, so
      // they read indented beneath the call. Leaders sit under the current rubric.
      // Consecutive responses share that leader; a response with no leader before
      // it stays under the rubric.
      let parent = currentRubric;
      if (type === 'response' && last) {
        if (last.type === 'spoken') parent = last.tempId;
        else if (last.type === 'response') parent = last.parentTempId;
      }
      if (last && last.type === type && last.parentTempId === parent) {
        last.lines.push({ runs: [{ text }] }); // same speaker continues → one box
        node = last;
      } else {
        node = add(type, text, parent);
        if (type === 'response') counts.congregation++;
        else counts.leader++;
      }
    }
    last = node;

    for (const fid of p.footnoteIds) {
      const body = footnotes.get(fid);
      if (!body) continue;
      commentary.push({
        targetTempId: node.tempId,
        kind: 'discussion',
        audience: 'commission',
        body,
      });
      counts.notes++;
    }
  }

  const doc: ImportDocument = {
    title: title || 'Imported document',
    subtitle: null,
    genre: 'liturgy',
    nodes,
    commentary,
  };
  return { doc, counts };
}
